// SourcePicker — the `field_sources` control (D42): a searchable multi-select
// over the library's sources, pure apart from view-ephemeral state.
//
// The agent never lists sources itself; the host projects the library's
// current sources in, so the list can't be stale or partial. The picks live in
// the form draft (ids, under the field key) like a multi-choice field.
//
// WAI-ARIA combobox with a multi-select listbox: the search Input keeps DOM
// focus and points at the active row via `aria-activedescendant` (the
// ComposerListbox / CommandPalette precedent). Arrow keys, Enter and Escape act
// only inside this focused control; no chord is registered anywhere. The list
// renders inline under the input (not floating), so the card's scroll cap
// never clips it. Selected sources show as removable chips above the search.

import { exhaustSwitchCase } from "@bilda/utils";
import { useLingui } from "@lingui/react/macro";
import { type KeyboardEvent, type ReactNode, useState } from "react";
import {
    filterSources,
    listKey,
    SOURCE_SEARCH_MAX,
} from "../../../app/yourSources.js";
import { Icon } from "../../Icon/Icon.js";
import { IconButton } from "../../IconButton/IconButton.js";
import { Input } from "../../Input/Input.js";
import { Text } from "../../Text/Text.js";
import type { FormSourceOption } from "../types.js";

export type SourcePickerProps = {
    readonly idBase: string;
    readonly label: string;
    readonly sources: readonly FormSourceOption[];
    readonly selected: readonly string[];
    readonly onToggle: (sourceId: string) => void;
    readonly size: "sm" | "md";
};

export const SourcePicker = ({
    idBase,
    label,
    sources,
    selected,
    onToggle,
    size,
}: SourcePickerProps): ReactNode => {
    const { t } = useLingui();
    // View-ephemeral: the search text, whether the list shows, the keyboard row.
    const [query, setQuery] = useState("");
    const [open, setOpen] = useState(false);
    const [active, setActive] = useState(0);

    const listboxId = `${idBase}-sources`;
    const optionId = (i: number): string => `${listboxId}-${i}`;
    const matches = filterSources(sources, query);
    const activeIndex = Math.max(0, Math.min(active, matches.length - 1));
    const picked = sources.filter((s) => selected.includes(s.id));

    const onKeyDown = (event: KeyboardEvent<HTMLInputElement>): void => {
        if (event.key === "Escape") {
            if (!open) return;
            event.preventDefault();
            setOpen(false);
            return;
        }
        // Enter acts only while the list shows; arrows also open it.
        if (event.key === "Enter" && !open) return;
        const action = listKey(event.key, activeIndex, matches.length);
        switch (action.type) {
            case "move":
                event.preventDefault();
                setOpen(true);
                setActive(action.index);
                return;
            case "toggle": {
                const row = matches[action.index];
                if (row === undefined) return;
                event.preventDefault();
                onToggle(row.id);
                return;
            }
            case "none":
                return;
            default:
                exhaustSwitchCase(action);
        }
    };

    return (
        <div className="bilda-source-picker">
            {picked.length > 0 ? (
                <ul
                    className="bilda-source-picker__chips"
                    aria-label={t`Selected sources`}
                >
                    {picked.map((s) => (
                        <li key={s.id} className="bilda-source-picker__chip">
                            <Text as="span" size="sm">
                                {s.title}
                            </Text>
                            <IconButton
                                size="sm"
                                label={t`Remove ${s.title}`}
                                onClick={() => onToggle(s.id)}
                            >
                                <Icon name="close" size="xs" />
                            </IconButton>
                        </li>
                    ))}
                </ul>
            ) : null}
            <Input
                size={size}
                role="combobox"
                aria-label={label}
                aria-expanded={open}
                aria-controls={listboxId}
                aria-autocomplete="list"
                aria-activedescendant={
                    open && matches.length > 0
                        ? optionId(activeIndex)
                        : undefined
                }
                placeholder={t`Search sources`}
                maxLength={SOURCE_SEARCH_MAX}
                value={query}
                onChange={(e) => {
                    setQuery(e.target.value);
                    setActive(0);
                    setOpen(true);
                }}
                onFocus={() => setOpen(true)}
                onBlur={() => setOpen(false)}
                onKeyDown={onKeyDown}
            />
            {open ? (
                <div
                    id={listboxId}
                    role="listbox"
                    aria-label={label}
                    aria-multiselectable="true"
                    className="bilda-source-picker__list"
                >
                    {matches.length === 0 ? (
                        <div
                            role="option"
                            tabIndex={-1}
                            aria-disabled="true"
                            aria-selected="false"
                            className="bilda-source-picker__empty"
                        >
                            <Text as="span" size="sm" tone="dim">
                                {sources.length === 0
                                    ? t`No uploaded sources yet`
                                    : t`No sources match`}
                            </Text>
                        </div>
                    ) : (
                        matches.map((s, i) => {
                            const isPicked = selected.includes(s.id);
                            return (
                                <div
                                    key={s.id}
                                    id={optionId(i)}
                                    role="option"
                                    // Not a tab stop: the input keeps focus
                                    // and points here (activedescendant).
                                    tabIndex={-1}
                                    aria-selected={isPicked}
                                    data-active={i === activeIndex || undefined}
                                    className="bilda-source-picker__option"
                                    // mousedown, not click: the input keeps
                                    // focus, so the list stays open for more
                                    // picks.
                                    onMouseDown={(event) => {
                                        event.preventDefault();
                                        onToggle(s.id);
                                    }}
                                    onMouseEnter={() => setActive(i)}
                                >
                                    <span
                                        className="bilda-source-picker__check"
                                        aria-hidden="true"
                                    >
                                        {isPicked ? (
                                            <Icon name="check" size="xs" />
                                        ) : null}
                                    </span>
                                    <Text as="span" size="sm">
                                        {s.title}
                                    </Text>
                                </div>
                            );
                        })
                    )}
                </div>
            ) : null}
        </div>
    );
};
